import {castArray} from 'lodash';
import {transformReference} from '@/engine/renderer/pdf/reference';
import {ValueTransformer, ValueResolver, VisibilitySet} from '@/engine/types';
import {applyStripOptions} from '@/engine/utils/string';
import {SupportedLocale} from '@/initializers/i18next';
import {logger} from '@/initializers/logger';
import {Mapper} from '@/model/types';
import {DOMAIN_VALUE_PREFIX} from '@/model/types/constants';

const normalizeSplittedField = (fieldName: string) => {
  const [, splitFieldName, splitIndex, rest] = Array.from(
    fieldName.match(/^(B5|H5|P2|V6|Q1|D36|D45)#(\d+)(.+)?$/) || [],
  );
  return splitFieldName ? `${splitFieldName}%${splitIndex}${rest ?? ''}` : fieldName;
};

// Change syntax to distinguish between index, field and condition
export const normalize = (fieldName: string): string => {
  let normalizedField = fieldName
    .replace(/USER(\d+)_dv3_0/g, 'USER$1=true')
    .replace(/USER(\d+)_dv3_1/g, 'USER$1=false')
    .replace(/CONS(\d+)_dv3_0/g, 'CONS$1=true')
    .replace(/CONS(\d+)_dv3_1/g, 'CONS$1=false')
    .replace(/_dv(\d+)_(\d+)(-\d)?/g, '=DV$1#$2')
    .replace(/-/g, '#')
    .replace(/PTP_0/g, 'PTP=DV3#1')
    .replace(/PTP_1/g, 'PTP=DV3#0');

  normalizedField = normalizedField
    .split('|')
    .map(i => normalizeSplittedField(i))
    .join('|');

  return normalizedField;
};

export const normalizeSignature = (value: string): string => {
  return value.replace(/_.+/g, '');
};

const getMapperTransform = <T extends string | string[]>(
  transform: Mapper,
  valueResolver: ValueResolver,
): ValueTransformer<T> | undefined => {
  const operations = [transform.operation].concat(transform?.operations || []).filter(Boolean);

  if (operations.length) {
    return value => {
      let castValue = castArray(value)[0] as T;

      for (const operation of operations) {
        if (operation?.strip) {
          castValue = applyStripOptions(String(castValue), operation.strip) as T;
        }

        if (operation?.replace) {
          const [from, to] = [operation.replace[0], operation.replace[1]];
          castValue = String(castValue).replaceAll(from, to) as T;
        }

        if (operation?.castValueAsNull) {
          const option = operation.castValueAsNull;
          const nullValue = typeof option === 'object' && '$ref' in option ? valueResolver(option) : option;

          castValue = (castValue === nullValue ? '' : castValue) as T;
        }

        if (operation?.stripDateYearPrefix) {
          castValue = String(castValue).replace(/^20(\d\d)(.+)?/, '$1$2') as T;
        }

        if (operation?.format) {
          switch (operation.format) {
            case 'tel': {
              if (Array.isArray(castValue)) return castValue;
              if (castValue.replace(/[^0-9]/g, '').length == 10) {
                const cleaned = castValue.replace(/[^0-9]/g, '');

                return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}` as T;
              }
            }
          }
        }
      }

      return castValue;
    };
  }

  return;
};

const separatorForField = (fieldname: string) => {
  switch (fieldname) {
    case 'P2':
      return ' - ';
    case 'B22&B5':
    case 'H17&H5':
      return '-';
    default:
      return ' ';
  }
};

/**
 * Resolves a field's value based on a defined syntax used for the field name.
 *
 * The parser function processes field names in the following order:
 * 1. If the field name contains a nested expression '()', it evaluate the expression and returns its value in {} before continuing evaluation of the field name.
 * 2. If the field name contains a '|' or '/', it splits the field name by these characters and evaluates alternatives, returning the first non-empty result.
 * 3. If the field name contains a '+', it splits the field name by the '+' character and evaluates conditions. If any condition is 'false', returns an empty string.
 * 4. If the field name contains a '+!', it splits the field name by the '+!' character and evaluates conditions. If any condition is not 'false', returns an empty string.
 * 5. If the field name contains a '+?', it splits the field name by the '+?' character and evaluates conditions. If field is not evaluated, default to 'true'. If any condition is 'false', returns an empty string.
 * 6. If the field name contains a '~', it splits the field name by the '~' character and checks if any condition is truthy. Returns 'true' if any is truthy, else 'false'.
 * 7. If the field name contains a '&', it splits the field name by the '&' character and concatenates the results, trimming each part and joining them with the appropriate separator.
 * 8. If the field name contains an '=', it splits the field name by the '=' character and compares the resolved values, returning 'true' if they match, otherwise 'false'.
 * 9. If the field name contains a '%', it splits the field into multiple fields based on a space character, returning the specified part.
 * 10. If the field name contains a '@', it splits the field name by the '@' character and resolves the field value using the specified property (e.g. 'acronym') of the domain value.
 * 11. Handles specific date-related field names, returning formatted date components.
 * 12. Checks for known field formats and logs a warning if the format is unknown.
 * 13. Transforms the field name into a reference for the value resolver.
 * 14. Uses the provided mappers to resolve field values if available.
 * 15. If no mapper is found, it uses the valueResolver to resolve the field value.
 */
export const createFieldParser = (mappers: Mapper[], valueResolver: ValueResolver, locale: SupportedLocale) => {
  return function parser(fieldName: string, visibilitySet: VisibilitySet): string {
    // Field starting with underscore are resolved in a custom mapping instruction after all parsers are done.
    if (fieldName.startsWith('_')) return '';

    // Support {value} for already resolved value in the expression. Used in parentheses match to have nested expressions.
    const [, evaluatedValue] = Array.from(fieldName.match(/^\{([^{}]*)\}$/) ?? []);
    if (evaluatedValue !== undefined) {
      return evaluatedValue;
    }

    // Handle alternatives before parentheses if they exist at the top level
    // This ensures expressions like "(A&B)|(C&D)" are split correctly
    if (fieldName.includes('|') || fieldName.includes('/')) {
      // Check if the | or / is at the top level (not inside parentheses)
      let depth = 0;
      let hasTopLevelAlternative = false;
      for (let i = 0; i < fieldName.length; i++) {
        const char = fieldName[i];
        if (char === '(') depth++;
        else if (char === ')') depth--;
        else if (depth === 0 && (char === '|' || char === '/')) {
          hasTopLevelAlternative = true;
          break;
        }
      }

      if (hasTopLevelAlternative) {
        // Split by the | or / character
        const alternatives = fieldName.split(/[|/]/);

        // Evaluate the alternatives, treat 'autre' and 'other' as null because those fields are
        // often used in alternative to show another field if the 'other' option is selected.
        // Treat 'false' as null to allow alternatives on evaluated conditions.
        for (const alternative of alternatives) {
          const value = parser(alternative, visibilitySet);
          if (value && !['autre', 'other', 'false'].includes(value.toLowerCase())) return value;
        }

        return '';
      }
    }

    // Use parentheses to have nested expression to evaluate before other expressions. "B1&B2(B3|B4)&B5" will evaluate B3 or B4 first, then concatenate the expression with B2 and B5.
    const [, leftEvaluate, evaluate, rightEvaluate] = Array.from(fieldName.match(/(.+)?\((.*|.*)\)(.+)?/) ?? []);
    if (evaluate) {
      const value = parser(evaluate, visibilitySet);
      if (!value) return parser([leftEvaluate, rightEvaluate].filter(Boolean).join(''), visibilitySet);
      return parser([leftEvaluate, `{${value}}`, rightEvaluate].filter(Boolean).join(''), visibilitySet);
    }

    if (fieldName.includes('+!')) {
      // Split by the +! character, left is value and right is condition
      const [value, ...parts] = fieldName.split('+!');
      const conditions = parts.map(part => parser(part, visibilitySet));
      if (conditions.some(condition => !['', 'false'].includes(condition))) return '';
      return parser(value, visibilitySet);
    }

    if (fieldName.includes('+?')) {
      // Split by the +? character, left is value and right is condition
      const [value, ...parts] = fieldName.split('+?');
      const conditions = parts.map(part => {
        const [, fieldCondition] = Array.from(part.match(/^([A-Z]+\d+)=\w+#\d+/) ?? []);
        if (fieldCondition && !visibilitySet.has(fieldCondition)) return 'true';

        return parser(part, visibilitySet);
      });
      if (conditions.some(condition => ['', 'false'].includes(condition))) return '';
      return parser(value, visibilitySet);
    }

    if (fieldName.includes('+')) {
      // Split by the + character, left is value and right is condition
      const [value, ...parts] = fieldName.split('+');
      const conditions = parts.map(part => parser(part, visibilitySet));

      if (conditions.some(condition => ['', 'false'].includes(condition))) return '';
      return parser(value, visibilitySet);
    }

    if (fieldName.includes('~')) {
      // Split by the ~ character, if any are truthy, return truthy
      const conditions = fieldName.split('~').map(part => parser(part, visibilitySet));
      if (conditions.some(condition => condition === 'true')) return 'true';
      return 'false';
    }

    if (fieldName.includes('&')) {
      // Split by the & character and concat
      return fieldName
        .split('&')
        .filter(Boolean)
        .map(part => parser(part, visibilitySet))
        .filter(Boolean)
        .map(part => part.trim())
        .join(separatorForField(fieldName));
    }

    if (fieldName.includes('=')) {
      const [key, domainValue] = fieldName.split('=');
      const resolvedValue = parser(key, visibilitySet);
      const expectedValue = domainValue.startsWith(DOMAIN_VALUE_PREFIX)
        ? parser(domainValue.replace(DOMAIN_VALUE_PREFIX, DOMAIN_VALUE_PREFIX + '-'), visibilitySet)
        : domainValue;

      if (!resolvedValue) return 'false';
      if (typeof resolvedValue === 'object')
        return Array.from(resolvedValue).includes(expectedValue) ? 'true' : 'false';

      return resolvedValue === expectedValue ? 'true' : 'false';
    }

    // Some fields needs to be split in multiple fields. Like B5-0 and B5-1 are B5 splitted by a space.
    if (fieldName.includes('%')) {
      const [key, splitIndex] = fieldName.split('%');
      const value = (valueResolver({$ref: key}, {visibilitySet}) ?? '').split(separatorForField(key));
      return splitIndex === '0' ? value[0] : value.slice(1, value.length).join(separatorForField(key));
    }

    if (fieldName.includes('@')) {
      const [key, property] = fieldName.split('@');
      return valueResolver({$ref: key, property}, {visibilitySet}) ?? '';
    }

    const currentDate = new Date();
    const dateFormatHandlers = {
      'DATE#1': () => currentDate.getDate().toString(),
      'DATE#2': () => currentDate.toLocaleString(locale, {month: 'long'}),
      'DATE#3': () => currentDate.getFullYear().toString().slice(-2),
      'DATE#4': () => currentDate.toISOString().split('T')[0].replace(/-/g, ''),
      'DATE#5': () => currentDate.toISOString().split('T')[0].replace(/-/g, ''),
      'COMPLETE_DATE': () => currentDate.toISOString().split('T')[0].replace(/-/g, ''),
    } as Record<string, () => string>;

    if (fieldName in dateFormatHandlers) {
      return dateFormatHandlers[fieldName]();
    }

    if (fieldName === 'true') return 'true';
    if (fieldName === 'false') return 'false';

    const isKnownFieldFormat =
      fieldName === 'PTP' ||
      fieldName.match(/(guarantee|index_name|CPG)#\d+/) ||
      fieldName.match(/[A-Z]+\d+/) ||
      fieldName.match(/[A-Z]+_benevaCode/) ||
      fieldName.startsWith(DOMAIN_VALUE_PREFIX);

    if (!isKnownFieldFormat) {
      logger.warn('Unknown field format', fieldName);
    }

    const mapper = mappers.find(({field}) => {
      const [, indexMatch] = Array.from(fieldName.match(/^([A-Z]+\d+)#\d+$/) ?? []);
      return indexMatch ? field === `${indexMatch}#{$index}` : field === fieldName;
    });

    const reference = transformReference({$ref: fieldName}, valueResolver);

    if (mapper) {
      const resolvedValue = valueResolver<string>(mapper.value ?? reference, {
        visibilitySet,
        transform: getMapperTransform(mapper, valueResolver),
      });
      return resolvedValue ?? '';
    }

    return valueResolver(reference, {visibilitySet}) ?? '';
  };
};
